import { Box, Typography } from "@mui/material";
import { useMember } from "api/members";
import { BoxSection } from "components/BoxSection/BoxSection";
import ProgressIndicator from "components/ProgressIndicator/ProgressIndicator";
import { OLTabs } from "components/Tabs/OLTabs";
import { useMessage } from "hooks/intl";
import { OLPaper } from "OLPaper";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CampaignTriggers } from "types/campaigns";
import { alphabetizeByProperty } from "utilities/arrays";
import { OLDate } from "utilities/dateFormat";
import { useStyles } from "../SingleMember.styles";
import MemberLeaderboardTable from "./MemberLeaderboardTable";

export function Leaderboards() {
  const classes = useStyles();
  const { message } = useMessage();
  const { id: memberID } = useParams<{ id: string }>();
  const [selectedLeaderboardId, setSelectedLeaderboardId] = useState<
    string | false
  >(false);

  const { useMembersCampaigns } = useMember(memberID as string);
  const { data: memberLeaderboards } = useMembersCampaigns({
    filters: [
      {
        query: "trigger",
        value: CampaignTriggers.LEADERBOARD,
        condition: "eq",
      },
    ],
  });

  const { useMemberLeaderboard } = useMember();
  const { data: currentMemberLeaderboard } = useMemberLeaderboard(
    selectedLeaderboardId || "",
    memberID || "",
  );

  const leaderboardTabs: {
    id: string;
    name: string;
  }[] = useMemo(
    () =>
      alphabetizeByProperty(
        memberLeaderboards?.items?.map(({ campaignId, name }) => ({
          id: campaignId,
          name,
        })),
        "name",
      ),
    [memberLeaderboards],
  );

  useEffect(() => {
    if (leaderboardTabs?.length > 0 && !selectedLeaderboardId) {
      setSelectedLeaderboardId(leaderboardTabs[0].id);
    }
  }, [leaderboardTabs, selectedLeaderboardId]);

  const currentLeaderboardName = memberLeaderboards?.items?.find(
    (leaderboard) => leaderboard.campaignId === selectedLeaderboardId,
  )?.name;

  const currentLeaderboardItems = [
    {
      label: "leaderboardName",
      value: currentLeaderboardName,
    },
    {
      label: "trackedMetric",
      value: message({ id: "numberOfActiveUnitsInWallet" }),
    },
    {
      label: "memberPosition",
      value: (
        <Box className={classes.positionContainer}>
          <Typography className={classes.position}>
            {currentMemberLeaderboard?.position}
          </Typography>
          <ProgressIndicator
            progress={currentMemberLeaderboard?.progress?.general as number}
          />
        </Box>
      ),
    },
    {
      label: "promotionDate",
      value: OLDate(currentMemberLeaderboard?.changedPositionAt),
    },
    {
      label: "currentScore",
      value: currentMemberLeaderboard?.score,
    },
  ];

  return (
    <>
      <Box className={classes.titleSection}>
        <Typography
          className={classes.header}
          data-test-id="single-member-leaderboards-title"
        >
          {message({ id: "leaderboards" })}
        </Typography>
      </Box>

      {leaderboardTabs?.length > 0 && (
        <>
          <OLTabs
            className={classes.bottomSpacing}
            items={leaderboardTabs}
            maxLabelLength={30}
            onChange={(_, tab: string) => {
              setSelectedLeaderboardId(tab);
            }}
            value={selectedLeaderboardId}
          />

          {currentMemberLeaderboard && (
            <>
              <OLPaper noPadding className={classes.bottomSpacing}>
                <BoxSection items={currentLeaderboardItems} rowsPerPage={5} />
              </OLPaper>
              <MemberLeaderboardTable
                currentMemberLeaderboard={currentMemberLeaderboard}
                selectedLeaderboardId={selectedLeaderboardId}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
